/*
 * Class strings for the app surfaces. See DESIGN.md.
 *
 * A constant here styles a surface; a component in this directory carries the
 * structure that goes with it. Reach for the component first — Panel, RowList,
 * Button, Badge, Input, Select — and for the constant only when you need to
 * override one utility, which you do by composing it with cn().
 */

/**
 * The page container. `max-w-6xl px-6` is the header's own inner container, so
 * page content lines up with the logo rather than merely near it.
 */
export const SHELL_WIDE = "mx-auto w-full max-w-6xl px-6 py-6";

/** The same rhythm for a single column of prose or a single run. */
export const SHELL_NARROW = "mx-auto w-full max-w-3xl px-6 py-6";

/** A panel: one step off the page, a hairline, 12px corners. Clips its rows. */
export const PANEL =
	"overflow-hidden rounded-panel border border-rule bg-surface-1";

/** The panel's title bar. The heading lives here; so does any panel-level control. */
export const PANEL_HEADER =
	"flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-rule px-4 py-2.5";

/** Panel body for prose or a form rather than a row list. */
export const PANEL_BODY = "px-4 py-3";

/**
 * A single line of setup above the work: the folder that was dropped, what a
 * batch shares. A panel's geometry on `surface-2` rather
 * than `surface-1` — it is chrome for the screen below it, not a panel of its
 * own, so it sits with the page rather than standing off it.
 */
export const BAND = "rounded-panel border border-rule bg-surface-2 px-4 py-3";

/**
 * A row inside a panel. Full-bleed, hairline below, hover lifts one rung. Sets
 * the content size itself: a row that inherited the page's 16px sat a size
 * above the rows in the panel beside it, and nothing on the screen said why.
 */
export const ROW =
	"border-b border-rule px-4 py-2 text-sm transition-colors last:border-b-0 hover:bg-surface-2 motion-reduce:transition-none";

/** A row that is the current one. Selection is lift, not a marker. */
export const ROW_SELECTED = "bg-surface-2";

/**
 * The row a modal was opened from, seen behind that modal. ROW_SELECTED is the
 * right weight for a list the reader is standing in, but a backdrop washes the
 * whole screen toward ink and compresses the ladder with it: one rung of lift
 * stops being a rung, and the reader cannot see which workflow the drawer in
 * front of them belongs to. The accent survives, because it is the only thing
 * on the page with any chroma and the wash has none. Still a lift and not a
 * marker — the name wears the accent the way a current chip does.
 */
export const ROW_OPEN = "bg-surface-2 font-medium text-accent";

/**
 * The link stretched over a row whose own element cannot be an anchor. The row
 * has to be `relative`, and any control inside it `relative` too, so it stacks
 * above this. See RowLink.svelte, which is what you should reach for.
 */
export const ROW_LINK_OVERLAY = "absolute inset-0";

/**
 * The column-header strip above a row list, and the group header inside one.
 * Chrome, so it is quiet rather than loud: 12px and grey, like every other
 * label. See the typography note in DESIGN.md for why nothing here shouts.
 */
export const ROW_HEADER =
	"border-b border-rule bg-surface-2 px-4 py-1.5 text-xs font-medium text-muted";

/**
 * A status pill. Sits on rung 3 so a hovered row, on rung 2, never swallows it.
 *
 * `justify-self-start` because a badge is usually a cell of a row grid, and a
 * grid item that is `inline-flex` is blockified to `flex` — `justify-self` then
 * defaults to stretch, and the pill grows to the full width of its column. That
 * made "Failed" and "Succeeded" render as identical slabs with a tail of empty
 * pill after the word. The declaration is inert anywhere else: `justify-self`
 * is ignored on a flex item and on a block child.
 */
export const BADGE =
	"inline-flex items-center justify-self-start gap-1.5 rounded-full bg-surface-3 px-2 py-0.5 text-xs leading-4 whitespace-nowrap";

/**
 * What a panel says when it holds nothing. The panel's own body padding plus a
 * line of breathing room, in the meta colour: an empty list is an ordinary
 * state here, not an event, so it is not centred in a large blank box.
 */
export const EMPTY_STATE = "px-4 py-6 text-sm text-muted";

/** A label beside a value, a count, a timestamp — anything read second. */
export const META = "text-xs text-muted";

/** A tab or filter. Idle carries a transparent border so selection adds no pixel. */
export const CHIP =
	"inline-flex items-center gap-1.5 rounded-control border px-2.5 py-1 text-xs transition-colors motion-reduce:transition-none";
export const CHIP_IDLE =
	"border-transparent text-muted hover:bg-surface-2 hover:text-ink";
export const CHIP_SELECTED = "border-rule bg-surface-2 text-ink";

/** A text or search input. Focus is the global outline; the border firms up. */
export const INPUT =
	"w-full rounded-control border border-rule bg-surface-1 px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-rule-strong";

/**
 * A floating surface — menu, popover, dialog. No shadow: the stronger hairline
 * and one rung of lift carry it.
 */
export const OVERLAY =
	"overflow-hidden rounded-panel border border-rule-strong bg-surface-2";

/** Menu item inside an OVERLAY. Rung 3 on hover, since the menu is already on 2. */
export const MENU_ITEM =
	"block w-full px-3 py-2 text-left text-sm text-muted transition-colors hover:bg-surface-3 hover:text-ink motion-reduce:transition-none";

/** The one filled action. `text-page` inverts correctly in both themes. */
export const BUTTON_PRIMARY =
	"inline-flex items-center gap-2 rounded-control bg-accent px-3.5 py-2 text-sm font-medium text-page transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none";

/** A neutral action: a lifted rung with a hairline. */
export const BUTTON_SECONDARY =
	"inline-flex items-center gap-2 rounded-control border border-rule bg-surface-1 px-3.5 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-2 motion-reduce:transition-none";

/**
 * A standing note on a panel: something the reader should know before acting,
 * not something that has gone wrong. Amber asks for care.
 */
export const NOTICE =
	"rounded-panel border border-rule bg-surface-1 px-4 py-3 text-sm text-notice";

/** The same box for plain information: nothing to be careful about, nothing gone wrong. */
export const NOTICE_INFO =
	"rounded-panel border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-accent";

/** The same box for something that failed. */
export const NOTICE_FAILED =
	"rounded-panel border border-rule bg-surface-1 px-4 py-3 text-sm text-state-failed";

/**
 * Discard or Restore on a finding: the one decision the review makes, small
 * enough to sit on a card or on a row the mapreader proposed it from.
 */
export const DECISION_BUTTON =
	"rounded-control border border-rule px-3 py-1 text-sm text-muted hover:text-ink";

/** A link inside running text or a row: the accent, underlined when reached for. */
export const LINK = "text-accent hover:underline";

/**
 * A small standing action worded as a link rather than drawn as a button — "Se
 * alle", "Overstyr", "Tøm". `font-medium` like every other 12px label: the five
 * call sites that predate this constant reached for `font-semibold`, which put
 * the quietest thing on the panel at the heaviest weight on it.
 */
export const LINK_ACTION = "text-xs font-medium text-accent hover:underline";

/** An identifier the reader copies rather than reads. */
export const CODE = "font-mono text-xs text-muted";

/**
 * A quiet name for something: a field above its value, a stat tile's caption, a
 * panel's own heading. One constant rather than three, because the three were
 * the same string and a divergence between them would be an accident.
 */
export const LABEL = "text-xs font-medium text-muted";

/** A select is an input with a caret. Same geometry, so a form row stays level. */
export const SELECT =
	"rounded-control border border-rule bg-surface-1 px-3 py-2 text-sm text-ink focus:border-rule-strong";

/**
 * The bar that holds a screen's committing action while the reader scrolls.
 * Opaque `bg-page` and no blur — the app header already owns that gesture — and
 * no shadow. The negative margin pairs with PageShell's padding so the hairline
 * spans the container rather than stopping short of it.
 */
export const ACTION_BAR =
	"sticky bottom-0 -mx-6 flex flex-wrap items-center justify-between gap-4 border-t border-rule bg-page px-6 py-3";

/**
 * A modal that takes the whole viewport. Its backdrop is tinted from `--ink` in
 * app.css, so it darkens in light and does not vanish against a near-black page
 * in dark. OVERLAY is the floating counterpart, for a menu or a popover.
 */
export const DIALOG_FULL =
	"fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none bg-page p-0 text-ink";

/**
 * A modal that holds one thing the reader picked, beside what they picked it
 * from. A full-screen sheet on a phone, where a side panel would leave the drop
 * zone no room, and a right-hand drawer from `lg` up. Same tinted backdrop as
 * DIALOG_FULL, so the catalogue behind it stays legible but plainly inactive.
 */
export const DIALOG_DRAWER =
	"fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none bg-page p-0 text-ink lg:left-auto lg:w-[28rem] lg:border-l lg:border-rule";

/**
 * An icon-only action inside a row or a dialog bar. No border and no fill until
 * it is reached for, so a column of them does not read as a column of buttons.
 */
export const BUTTON_GHOST =
	"inline-flex items-center rounded-control p-1 text-muted transition-colors hover:bg-surface-2 hover:text-ink motion-reduce:transition-none";

/** One measurement in a row of them. See StatTile.svelte. */
export const STAT_TILE =
	"rounded-panel border border-rule bg-surface-1 px-4 py-3";
export const STAT_VALUE = "mt-1 text-sm font-medium tabular-nums text-ink";

/*
 * Arrival. The keyframes live in app.css; these are the two ways to wear them.
 *
 * A CSS animation on element creation, never a Svelte `transition:` directive —
 * a keyed {#each} reuses its DOM, so the ten-second poll on /home and /runs
 * creates nothing and therefore animates nothing. See app.css and DESIGN.md.
 *
 * Set `--enter-index` on the element to place it in the stagger; the delay is
 * capped in CSS, so no call site does arithmetic.
 */

/** A panel arriving: fades and settles 4px. */
export const ENTER_PANEL =
	"app-enter app-enter-lift motion-reduce:animate-none";

/** A row arriving. Opacity only: a list of 14px type must not travel. */
export const ENTER_ROW = "app-enter motion-reduce:animate-none";

/**
 * The machine changed something — a meter's width, a state's colour. Slower
 * than a hover on purpose: nobody is looking at the row when it changes, and
 * peripheral vision catches a slow drift far better than an instant swap.
 */
export const TRANSITION_STATE =
	"duration-[var(--motion-state)] ease-out motion-reduce:transition-none";

/*
 * Words a change removes or adds, as Word's redline marks them. TextDiff draws
 * them; HTML built outside Svelte (the language edit's redline) uses the same
 * classes, so every diff on the site reads alike.
 */

/** Words the change removes. */
export const DELETED_TEXT = "rounded-chip bg-diff-delete text-ink line-through";

/** Words the change adds. */
export const INSERTED_TEXT =
	"rounded-chip bg-diff-insert text-ink no-underline";
